/**
 * Cookie consent state + Google Consent Mode v2 wiring.
 *
 * Why this exists:
 *   - GDPR + CCPA require explicit consent before non-essential cookies.
 *   - Google Consent Mode v2 is required by Google Ads (started enforcing
 *     in 2024) and Meta is moving the same direction.
 *   - We tell GTM/GA4/Meta about consent state via gtag('consent', ...) so
 *     they can either send full data or 'cookieless pings' depending.
 *
 * Storage:
 *   localStorage key shipmaster_consent_v1 holds the user's choice. When
 *   present, banner is hidden. Reset via `localStorage.removeItem(...)`
 *   in DevTools to retest.
 */

const CONSENT_KEY = 'shipmaster_consent_v1';

const DEFAULTS_DENIED = {
  ad_storage: 'denied',
  ad_user_data: 'denied',
  ad_personalization: 'denied',
  analytics_storage: 'denied',
  functionality_storage: 'granted',
  security_storage: 'granted',
};

const ALL_GRANTED = {
  ad_storage: 'granted',
  ad_user_data: 'granted',
  ad_personalization: 'granted',
  analytics_storage: 'granted',
  functionality_storage: 'granted',
  security_storage: 'granted',
};

/**
 * Push the initial 'denied' state to gtag BEFORE GTM/GA4 fire. This must
 * run as early as possible — index.html bootstraps gtag, then we call this
 * from React on first mount. If consent has already been given, we
 * immediately push the granted update.
 */
export function initConsentMode() {
  if (typeof window === 'undefined') return;
  window.dataLayer = window.dataLayer || [];
  function gtag() {
    window.dataLayer.push(arguments);
  }

  // Always default to denied — Consent Mode requires this baseline.
  gtag('consent', 'default', DEFAULTS_DENIED);

  // If returning user already consented, immediately upgrade.
  const stored = readConsent();
  if (stored?.choice === 'accept') {
    gtag('consent', 'update', ALL_GRANTED);
  } else if (stored?.choice === 'reject') {
    // Keep denied (already default) — explicit re-push for cleanliness.
    gtag('consent', 'update', DEFAULTS_DENIED);
  }
}

export function readConsent() {
  if (typeof window === 'undefined') return null;
  try {
    const raw = localStorage.getItem(CONSENT_KEY);
    if (!raw) return null;
    return JSON.parse(raw);
  } catch {
    return null;
  }
}

export function hasConsentChoice() {
  return !!readConsent()?.choice;
}

export function acceptConsent() {
  if (typeof window === 'undefined') return;
  const record = { choice: 'accept', set_at: new Date().toISOString() };
  try {
    localStorage.setItem(CONSENT_KEY, JSON.stringify(record));
  } catch {
    // localStorage blocked; consent state will revert to default-denied
    // on next page load. Still push the update for this session.
  }
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push(['consent', 'update', ALL_GRANTED]);
  // Convenience event for any custom dependents (e.g. delayed pixel loaders).
  window.dataLayer.push({ event: 'consent_accepted' });
}

export function rejectConsent() {
  if (typeof window === 'undefined') return;
  const record = { choice: 'reject', set_at: new Date().toISOString() };
  try {
    localStorage.setItem(CONSENT_KEY, JSON.stringify(record));
  } catch {
    /* ignore */
  }
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push(['consent', 'update', DEFAULTS_DENIED]);
  window.dataLayer.push({ event: 'consent_rejected' });
}

/** For users who want to revisit the choice (footer link). */
export function resetConsent() {
  if (typeof window === 'undefined') return;
  try {
    localStorage.removeItem(CONSENT_KEY);
  } catch {
    /* ignore */
  }
}
